import { distance } from '../core/math';
import { VEHICLE_SPECS } from '../vehicles/specs';
import { lastOrders, oneArmedBandits, theCollection } from './firm';
import { BORROWED_TIME, EXPORT_ORDER, GULLET_JOB, LAST_ORDERS, ONE_ARMED_BANDITS, THE_COLLECTION, WET_PAPER } from './ids';
import type { MissionScript } from './runner';
import { holdHint, leaveAMotor } from './shared';
import type { MissionDef, RewardLine } from './types';

/**
 * The jobs. All characters, places and lines are original to Borough Heat.
 *
 *   Mags Delaney     landlady of The Crooked Anchor; runs a quiet firm out of the back room.
 *   Ronnie & Clive Vane   the brothers who run the docks and The Velvet Lamp club.
 *   Big Terry        Ronnie Vane's minder.
 *   Wilf Tuckett     fence who works out of the railway arches.
 *   DS Holloway      Low Borough CID; wants you inside.
 *   Des Halloran     Halloran Motors, Cinder Yards — "export" orders.
 *
 * Jobs 4–6 (the firm's second arc) live in firm.ts, the phone-box street jobs in streetJobs.ts.
 *
 * House style for the text: the objective says what to do, the hint says how (and names the control
 * for the device in hand: `holdHint`, or "press E", which the HUD rewords for touch). Nobody who isn't
 * in the scene gets a line — Mags briefs you at the start and pays you at the end; on the job it's the
 * people around you and the police radio.
 *
 * Plain words where it matters: slang lives in the spoken lines only. Objectives, hints and fail reasons say
 * "police" (not the law / coppers / CID / a tail), "wanted level" (not heat), "car" (not motor), name places
 * as the map labels them and cars by colour + model; a fail or blocked reason says what went wrong and what
 * to do now. A blurb says who wants it, what you do and what it pays. Fail reasons must not use the words
 * src/ui/format.ts `failStamp` reads as an arrest or a knock-out (nick, arrest, caught, dead, killed…).
 * The voices, the period rules and the length limits: docs/WRITING.md.
 */

export { BORROWED_TIME, EXPORT_ORDER, GULLET_JOB, LAST_ORDERS, ONE_ARMED_BANDITS, STREET_JOB, THE_COLLECTION, WET_PAPER } from './ids';

/** Story order: each job unlocks the next. Jobs 4–6 (Mags' firm) also need the 'mags-firm' unlock. */
export const STORY: readonly string[] = [BORROWED_TIME, WET_PAPER, GULLET_JOB, ONE_ARMED_BANDITS, THE_COLLECTION, LAST_ORDERS];

const health01 = (m: MissionScript, key: string): number => {
  const v = m.vehicle(key);
  return v ? v.health / v.spec.maxHealth : 0;
};

// ----------------------------------------------------------------------------- 1. Borrowed Time

const VESPER_PAINT = 0x7d1d18;

/** The first job's tail starts this far off at least (and out of sight) until the job is done once. */
export const FIRST_TAIL_MIN_DISTANCE = 60;

/**
 * Borrowed Time is a new player's first chase, on a phone, in the first car they ever drove: until the job has
 * been done once, any police car nearer than FIRST_TAIL_MIN_DISTANCE or on screen is sent away before the level-1
 * tail starts, so the lesson is "lose a tail" rather than "outrun a car already at your bumper". Fresh units then
 * come in from off screen as usual (docs/FIRST-15-MINUTES.md #5).
 */
function gentleTail(m: MissionScript): void {
  if (m.ctx.services.tryGet('save')?.isMissionComplete(BORROWED_TIME)) return;
  const player = m.ctx.services.tryGet('player');
  if (player) m.ctx.services.tryGet('police')?.standBack(player.pos, FIRST_TAIL_MIN_DISTANCE);
}

export const borrowedTime: MissionDef = {
  id: BORROWED_TIME,
  title: 'Borrowed Time',
  client: 'Mags Delaney',
  tag: 'JOB 01 · VEHICLE',
  blurb:
    'Ronnie Vane owes Mags £800, so she’s taking his car instead. Steal his red Vesper GT from outside The Velvet Lamp and drive it to Lockup 7, Pilcher Ginnel. Mags pays £800, plus a bonus if there isn’t a scratch on it.',
  reward: 800,
  requires: [],
  giver: { poi: 'mission-start-anchor', prompt: 'Knock on Mags’ back-room door', glyph: 'M' },
  introFocus: { poi: 'club-velvet-lamp' },
  intro: [
    { who: 'Mags', text: 'Ronnie Vane parks his new Vesper outside the Velvet Lamp like he’s the Duke of Kessmouth.' },
    { who: 'Mags', text: 'He owes me eight hundred. I’ll take it in coachwork — Lockup 7, Pilcher Ginnel.' },
    { who: 'Mags', text: 'And love? Not a scratch. I’m selling it back to his brother.' },
  ],
  setup: (m) => {
    m.spawnVehicle('vesper', 'coupe', { poi: 'club-velvet-lamp', offset: 6 }, { locked: true, paint: VESPER_PAINT, parked: true });
  },
  protect: [{ vehicle: 'vesper', minHealth: 0.25, reason: 'The Vesper is smashed up, and Mags doesn’t pay for scrap. Try again and drive more carefully.' }],
  abandon: { vehicle: 'vesper', label: 'the Vesper', distance: 150, reason: 'You left the Vesper behind and somebody else drove off in it. Stay with the car until it’s in the lockup.' },
  stages: [
    {
      id: 'walk',
      kind: 'goTo',
      at: { vehicle: 'vesper' },
      radius: 12,
      text: 'Walk to The Velvet Lamp. Ronnie’s red Vesper GT is parked outside.',
      hint: 'It’s close by — follow the red marker on foot.',
      // There she is: a look at the car before the job starts in earnest (nobody's after you yet).
      onComplete: (m) => m.glance({ vehicle: 'vesper' }),
    },
    {
      id: 'steal',
      kind: 'enterVehicle',
      vehicle: 'vesper',
      text: 'Get into the red Vesper GT. It’s locked, so you’ll have to break in.',
      hint: 'Stand by the driver’s door and press E. Breaking in takes a few seconds.',
      onComplete: (m) => {
        m.say([
          { who: 'Big Terry', text: 'Oi! OI! That’s Mr Vane’s motor!' },
          { who: 'Big Terry', text: 'Somebody get on the blower to the law!' },
        ]);
        // First job: a local patrol comes looking (level 1) — enough to teach losing a tail.
        if (m.heat() < 1) {
          gentleTail(m);
          m.setHeat(1);
        }
      },
    },
    {
      id: 'lose-heat',
      kind: 'escapeHeat',
      text: 'Big Terry called the police. Lose them: get out of their sight.',
      hint: 'Turn corners, use alleys and side streets. Once they can’t see you, your wanted level drops.',
      checkpoint: {
        label: 'in the Vesper, police after you',
        restore: (m) => {
          const v = m.vehicle('vesper');
          if (!v || v.destroyed || v.health / v.spec.maxHealth < 0.6) {
            m.spawnVehicle('vesper', 'coupe', { poi: 'club-velvet-lamp', offset: 6 }, { paint: VESPER_PAINT });
          }
          m.placePlayer({ vehicle: 'vesper' }, 'vesper');
          // A retry starts the chase over, not with the car that caught you still at the bumper.
          gentleTail(m);
          m.setHeat(1);
        },
      },
    },
    {
      id: 'deliver',
      kind: 'goTo',
      at: { poi: 'lockup-pilcher-7' },
      radius: 8,
      vehicle: 'vesper',
      requireHeat0: true,
      text: 'Drive the Vesper to Lockup 7, Pilcher Ginnel, in Tanner’s Row.',
      blockedText: 'The police are still after you. Lose them first, then drive into the lockup.',
      hint: 'Mind the paintwork: no damage earns a bonus, a lot of damage costs you.',
    },
  ],
  outroFocus: { poi: 'lockup-pilcher-7' },
  outro: [
    { who: 'Mags', text: 'Lovely. Clive Vane’ll be buying his brother’s motor back by Friday.' },
    { who: 'Mags', text: 'Next, go to the phone box on The Parade and answer it when it rings. I’ve a parcel that needs carrying.' },
  ],
  payout: (m) => {
    const lines: RewardLine[] = [{ label: 'Mags’ payment', amount: 800 }];
    const h = health01(m, 'vesper');
    if (h >= 0.9) lines.push({ label: 'Not a scratch on it', amount: 200 });
    else if (h < 0.6) lines.push({ label: 'Damage repairs', amount: -150 });
    return lines;
  },
  unlocks: ['street-jobs'],
};

// ----------------------------------------------------------------------------- 2. Wet Paper

const PARCEL_TIME = 300;
/**
 * A checkpoint retry gets the time that was left when the attempt failed, never less than this:
 * enough to lose a level-1 tail and make the ~600 m run to the quay, but no longer enough for the
 * "still crisp" bonus (> 150 s at the drop). The parcel only gets a full fresh clock on Start again.
 */
export const PARCEL_RETRY_MIN = 150;

/** Parcel clock for a checkpoint retry, given the time left when the last attempt failed. */
export function parcelRetryTime(leftAtFail: number | undefined): number {
  if (leftAtFail === undefined) return PARCEL_TIME;
  return Math.min(PARCEL_TIME, Math.max(PARCEL_RETRY_MIN, leftAtFail));
}
/** The tip-off patrol turns up at least this far from the player (the "end of the yard"). */
export const TIPOFF_MIN_DISTANCE = 60;
const SOGGY = 'Out of time: the rain has turned the parcel to papier-mâché. Try again and get it to the quay faster.';

export const wetPaper: MissionDef = {
  id: WET_PAPER,
  title: 'Wet Paper',
  client: 'Mags Delaney',
  tag: 'JOB 02 · COURIER',
  blurb:
    'Mags needs a parcel moved. Collect it from Wilf Tuckett at the Arch 14 Lockup and take it to the Basin Crane Quay within 5 minutes, before the rain ruins it — with no police following you. Pays £1,200, more if you’re quick.',
  reward: 1200,
  requires: [BORROWED_TIME],
  giver: { poi: 'phone-parade', prompt: 'Answer the ringing phone', glyph: '☎' },
  introFocus: { poi: 'lockup-arch-14' },
  intro: [
    { who: 'Mags (phone)', text: 'It’s me. Don’t say my name, this line’s got more ears than a cornfield.' },
    { who: 'Mags (phone)', text: 'Wilf’s holding a parcel under Arch 14. Take it to the Basin Crane Quay.' },
    { who: 'Mags (phone)', text: 'It’s wrapped in the Evening Post and it’s been raining all week. Shift yourself.' },
  ],
  stages: [
    {
      id: 'arches',
      kind: 'goTo',
      at: { poi: 'lockup-arch-14' },
      radius: 14,
      text: 'Go to the Arch 14 Lockup, under the railway arches in Cinder Yards.',
      hint: 'It’s a long walk — take a car: press E next to one to get in.',
    },
    {
      id: 'collect',
      kind: 'hold',
      at: { poi: 'lockup-arch-14', offset: 2 },
      radius: 6,
      seconds: 1.5,
      prompt: 'Take the parcel off Wilf',
      text: 'Collect the parcel from Wilf at the Arch 14 Lockup.',
      hint: holdHint('Get out of the car and stand next to Wilf.'),
      lines: [{ who: 'Wilf', text: 'There she is. Mind, it’s damp — and so’s my nerve.' }],
      onComplete: (m) => {
        m.say([
          { who: 'Wilf', text: 'Hang about… there’s a jam sandwich at the end of the yard. Someone’s grassed!' },
          { who: 'Wilf', text: 'Go on, leg it! I never saw you!' },
        ]);
        // A tip-off, not a manhunt: one patrol knows where you are (level 1) and is on its way from the
        // far end of the yard (out of sight, >= 60 m), so there's a few seconds to get moving.
        m.setHeat(1);
        m.callPolice({ poi: 'lockup-arch-14' }, { minDistance: TIPOFF_MIN_DISTANCE });
        // Came on foot? Wilf's mate has left a motor by the arch — the same one a retry puts you in.
        leaveAMotor(m, { poi: 'lockup-arch-14', offset: 8 });
        m.startTimer(PARCEL_TIME, SOGGY);
      },
    },
    {
      id: 'shake',
      kind: 'escapeHeat',
      text: 'Somebody told the police. Lose them — and hurry, the parcel is on a timer.',
      hint: 'Get out of their sight. The canal bridges are narrow: easy to block, easy to get stuck on.',
      checkpoint: {
        label: 'parcel in hand, police after you',
        restore: (m) => {
          m.spawnVehicle('getaway', 'compact', { poi: 'lockup-arch-14', offset: 8 });
          m.placePlayer({ vehicle: 'getaway' }, 'getaway');
          m.releaseVehicle('getaway');
          m.setHeat(1);
          // The parcel has been out in the rain all this time: carry the clock over (MISSIONS-7).
          const left = m.vars.timeLeftAtFail;
          m.startTimer(parcelRetryTime(typeof left === 'number' ? left : undefined), SOGGY);
        },
      },
    },
    {
      id: 'drop',
      kind: 'goTo',
      at: { poi: 'docks-crane-quay' },
      radius: 11,
      requireHeat0: true,
      text: 'Take the parcel to the Basin Crane Quay in Gullet Basin.',
      blockedText: 'The police are following you. Lose them first, then go to the quay.',
    },
  ],
  outroFocus: { poi: 'docks-crane-quay' },
  outro: [
    { who: 'Docker', text: 'You the parcel? Blimey, it’s soggy. Still — it’s all there.' },
    { who: 'Docker', text: 'Mags says get yourself round the Anchor. She’s got something bigger on.' },
  ],
  payout: (m) => {
    const lines: RewardLine[] = [{ label: 'Delivery', amount: 1200 }];
    const left = typeof m.vars.timeLeftAtEnd === 'number' ? m.vars.timeLeftAtEnd : 0;
    if (left > 150) lines.push({ label: 'Fast delivery — still dry', amount: 300 });
    else if (left > 60) lines.push({ label: 'Quick delivery — only a bit damp', amount: 100 });
    return lines;
  },
};

// ----------------------------------------------------------------------------- 3. The Gullet Job

const VAN_PAINT = 0x4a5640;
const REGENT_PAINT = 0x8a877d;
/**
 * Seconds on the run before DS Holloway can take the radio (heat 4). He only does once a unit has eyes
 * on you after that: break line of sight by then and keep out of sight, and the run stays at level 3 —
 * the "clean getaway" bonus. (A flat 25 s meant level 4 every time, and a bonus nobody could earn.)
 */
export const HOLLOWAY_AFTER = 25;

/** Is a unit looking at the player right now (the heat service's line of sight)? */
const seen = (m: MissionScript): boolean => m.ctx.services.tryGet('heat')?.seen ?? true;

export const gulletJob: MissionDef = {
  id: GULLET_JOB,
  title: 'The Gullet Job',
  client: 'Mags Delaney',
  tag: 'JOB 03 · THE BIG ONE',
  blurb:
    'Mags wants the Vanes’ smuggled goods from Bonded Warehouse No. 3. Take the crew van from the Gasworks Yard, load the crates, escape the police when the alarm rings, move the goods into a clean grey Regent at the Arch 14 Lockup and bring it to The Crooked Anchor. Pays £2,500.',
  reward: 2500,
  requires: [WET_PAPER],
  giver: { poi: 'mission-start-anchor', prompt: 'Knock on Mags’ back-room door', glyph: 'M' },
  introFocus: { poi: 'docks-bonded-3' },
  intro: [
    { who: 'Mags', text: 'Bonded Warehouse Three. The Vanes keep their imported “whisky” in there. It isn’t whisky.' },
    { who: 'Mags', text: 'Crew van’s at the Gasworks. Load up, and when the bell goes — and it will — run.' },
    { who: 'Mags', text: 'There’s a clean Regent in Arch 14. Swap the gear, ditch the van, bring it home to me.' },
  ],
  setup: (m) => {
    m.spawnVehicle('crewvan', 'van', { poi: 'gasworks-yard' }, { ownedByPlayer: true, paint: VAN_PAINT, parked: true });
  },
  protect: [
    { vehicle: 'crewvan', minHealth: 0.01, reason: 'The crew van was destroyed — and the goods with it. Try again and keep the van in one piece.' },
    { vehicle: 'clean', minHealth: 0.01, reason: 'You wrecked the grey Regent. The goods are all over the road. Try again and drive calmly.' },
  ],
  abandon: { vehicle: 'crewvan', label: 'the crew van', distance: 180, reason: 'You abandoned the crew van, so the job’s off. Stay with the van until the goods are moved.' },
  stages: [
    {
      id: 'gasworks',
      kind: 'goTo',
      at: { vehicle: 'crewvan' },
      radius: 14,
      text: 'Go to the Gasworks Yard and find the crew van, a green Haulmaster.',
      hint: 'It’s Mags’ own van: it isn’t locked, and nobody will call the police.',
    },
    { id: 'board', kind: 'enterVehicle', vehicle: 'crewvan', text: 'Get in the green crew van.', hint: 'Stand next to it and press E.' },
    {
      id: 'drive-in',
      kind: 'goTo',
      at: { poi: 'docks-bonded-3', offset: 8 },
      radius: 14,
      vehicle: 'crewvan',
      text: 'Drive the van to Bonded Warehouse No. 3 at Gullet Basin.',
      hint: 'Drive normally. Nobody looks twice at a van.',
      // Pulled up at the door, all quiet: the last look round before the bell.
      onComplete: (m) => m.glance({ poi: 'docks-bonded-3', offset: 2 }),
    },
    {
      id: 'load',
      kind: 'hold',
      at: { poi: 'docks-bonded-3', offset: 2 },
      radius: 6,
      seconds: 7,
      prompt: 'Load the crates',
      text: 'Load the crates into the van. Loading sets off the alarm, and the police will come.',
      hint: holdHint('Stand at the warehouse door, on foot. Keep holding while the alarm rings.'),
      near: { vehicle: 'crewvan', radius: 24, text: 'Park the van next to the warehouse door first.' },
      checkpoint: {
        label: 'at the warehouse door',
        restore: (m) => {
          // Fresh van at the door unless the old one is still parked there in one piece (otherwise the
          // "abandoned the van" rule would fire the moment we're put back at the warehouse).
          const v = m.vehicle('crewvan');
          const door = m.resolve({ poi: 'docks-bonded-3', offset: 8 });
          if (!v || v.destroyed || Math.hypot(v.pos.x - door.x, v.pos.z - door.z) > 30) {
            m.spawnVehicle('crewvan', 'van', { poi: 'docks-bonded-3', offset: 8 }, { ownedByPlayer: true, paint: VAN_PAINT });
          }
          m.releaseVehicle('clean');
          m.vars.alarm = 0;
          m.setHeat(0);
          m.placePlayer({ poi: 'docks-bonded-3', offset: 4 });
        },
      },
      onProgress: (m, p) => {
        if (p > 0.15 && !m.vars.alarm) {
          m.vars.alarm = 1;
          m.say([
            { who: 'Alarm bell', text: 'BRRRRRRRING—', seconds: 1.6 },
            { who: 'Nightwatchman', text: 'Oi! Who’s that in there?! I’m calling the police!' },
          ]);
          m.setHeat(2);
        } else if (p > 0.6 && m.vars.alarm === 1) {
          m.vars.alarm = 2;
          m.setHeat(3);
        }
      },
      onComplete: (m) => {
        m.setHeat(3);
        m.say([{ who: 'Nightwatchman', text: 'Hear that? That’s the law, that is. You’re nicked, son — nicked!' }]);
      },
    },
    {
      id: 'run',
      kind: 'goTo',
      at: { poi: 'lockup-arch-14', offset: 6 },
      radius: 14,
      vehicle: 'crewvan',
      text: 'Drive the van to the Arch 14 Lockup. A clean grey Regent is waiting there.',
      hint: 'Get out of police sight fast. Still seen after 25 seconds? DS Holloway sends more cars.',
      onEnter: (m) => {
        m.spawnVehicle('clean', 'saloon', { poi: 'lockup-arch-14' }, { ownedByPlayer: true, paint: REGENT_PAINT, parked: true });
      },
      onTick: (m) => {
        if (m.stageTime > HOLLOWAY_AFTER && !m.vars.escalated && m.heat() >= 3 && seen(m)) {
          m.vars.escalated = true;
          m.setHeat(4);
          m.say([{ who: 'DS Holloway (radio)', text: 'All units, green Haulmaster van, Gullet Basin. I want that van, and I want the driver.' }]);
        }
      },
    },
    {
      id: 'swap',
      kind: 'hold',
      at: { vehicle: 'clean' },
      radius: 5,
      seconds: 3.5,
      prompt: 'Move the crates into the Regent',
      text: 'Move the crates from the van into the grey Regent.',
      hint: holdHint('Park the van next to the Regent, get out and stand by it.'),
      near: { vehicle: 'crewvan', radius: 26, text: 'Park the van closer to the grey Regent.' },
      onComplete: (m) => {
        // The van stays behind for the police to find; nobody's looking for a grey Regent.
        m.releaseVehicle('crewvan');
      },
    },
    { id: 'board-clean', kind: 'enterVehicle', vehicle: 'clean', text: 'Get in the grey Regent.' },
    {
      id: 'lose-them',
      kind: 'escapeHeat',
      text: 'The police are looking for a van, not a grey Regent. Lose any police still near you.',
      hint: 'Drive like a vicar: calmly. Speeding past a police car gets you noticed.',
      // The swap is the job's turning point: a wreck or an arrest after it shouldn't send you back to
      // the warehouse bell. A retry puts you in the Regent (a fresh one if it's gone) at level 2.
      checkpoint: {
        label: 'in the grey Regent, goods aboard',
        restore: (m) => {
          const v = m.vehicle('clean');
          const arch = m.resolve({ poi: 'lockup-arch-14' });
          if (!v || v.destroyed || distance(v.pos, arch) > 60) {
            m.spawnVehicle('clean', 'saloon', { poi: 'lockup-arch-14' }, { ownedByPlayer: true, paint: REGENT_PAINT });
          }
          m.releaseVehicle('crewvan');
          m.placePlayer({ vehicle: 'clean' }, 'clean');
          m.setHeat(2);
        },
      },
    },
    {
      id: 'home',
      kind: 'goTo',
      at: { poi: 'pub-crooked-anchor' },
      radius: 14,
      vehicle: 'clean',
      requireHeat0: true,
      text: 'Bring the goods to Mags at The Crooked Anchor.',
      blockedText: 'Don’t lead the police to Mags. Lose them first, then come back.',
    },
  ],
  outroFocus: { poi: 'pub-crooked-anchor' },
  outro: [
    { who: 'Mags', text: 'Well, well. Look what the cat dragged in — and look what it dragged.' },
    { who: 'Mags', text: 'Holloway’ll be eating his hat. Ronnie Vane’ll be eating his hat, his coat and his brother.' },
    { who: 'Mags', text: 'Here. A proper cut. You’re one of mine now, love.' },
  ],
  payout: (m) => {
    const lines: RewardLine[] = [{ label: 'Your cut', amount: 2500 }];
    if (!m.vars.escalated) lines.push({ label: 'Clean getaway — Holloway never joined in', amount: 500 });
    return lines;
  },
  unlocks: ['mags-firm'],
};

// ----------------------------------------------------------------------------- street job: export orders

/** Specs Halloran will ship, with his price. */
export const EXPORT_PRICES: readonly { spec: string; price: number }[] = [
  { spec: 'compact', price: 250 },
  { spec: 'saloon', price: 350 },
  { spec: 'cab', price: 300 },
  { spec: 'van', price: 300 },
  { spec: 'coupe', price: 550 },
  { spec: 'estate', price: 300 },
  { spec: 'roadster', price: 450 },
  { spec: 'exec', price: 700 },
];

/**
 * What Des says over an order (spoken: his trade talk). Pools, picked by the order number, so the same job
 * doesn't say the same thing eight times running; every intro still names the car, every outro the boat.
 */
export const EXPORT_INTROS: readonly ((name: string) => readonly [string, string])[] = [
  (name) => [`I need a ${name}. Any colour, as long as it’s somebody else’s.`, 'In one piece, mind. And if you bring the law into my yard I never met you.'],
  (name) => [`A ${name}, this time. The boat’s in Thursday with a space the shape of one.`, 'Clean, and nobody behind you. The yard gate’s open till I say it isn’t.'],
  (name) => [`The man across the water wants a ${name}. Don’t ask me why; he pays.`, 'Drive it in gentle. He counts the dents and takes them off my end.'],
  (name) => [`A ${name}. There’s one on every other street. Find me one nobody’s watching.`, 'No coppers on your tail when you come in. My neighbours have long memories and short telephones.'],
];
export const EXPORT_OUTROS: readonly string[] = [
  'Lovely. She’ll be across the water by the weekend.',
  'Keys. Ta. By Friday she’s somebody else’s problem, in somebody else’s country.',
  'That’ll do nicely. Plates off, numbers off, and she was never here.',
  'Good lad. The boat’ll have her, and I’ll have forgotten your face by Monday.',
];

/** Halloran's order number `n` (rotates through the list). */
export function exportOrder(n: number): MissionDef {
  const order = EXPORT_PRICES[((n % EXPORT_PRICES.length) + EXPORT_PRICES.length) % EXPORT_PRICES.length]!;
  const name = VEHICLE_SPECS[order.spec]?.displayName ?? order.spec;
  const k = ((n % EXPORT_INTROS.length) + EXPORT_INTROS.length) % EXPORT_INTROS.length;
  const [say1, say2] = EXPORT_INTROS[k]!(name);
  return {
    id: EXPORT_ORDER,
    title: `Export: ${name}`,
    client: 'Des Halloran',
    tag: 'STREET JOB · EXPORT',
    blurb: `Des Halloran ships stolen cars abroad. Steal any ${name} and drive it into Halloran Motors in Cinder Yards, not badly damaged and with no police watching. Pays £${order.price}.`,
    reward: order.price,
    requires: [BORROWED_TIME],
    repeatable: true,
    giver: { poi: 'garage-halloran', prompt: 'Ask Halloran about export orders', glyph: '£' },
    intro: [
      { who: 'Des Halloran', text: say1 },
      { who: 'Des Halloran', text: say2 },
    ],
    stages: [
      {
        id: 'bring',
        kind: 'goTo',
        at: { poi: 'garage-halloran' },
        radius: 11,
        vehicleSpec: order.spec,
        minHealth: 0.5,
        requireUnseen: true,
        text: `Steal a ${name} and drive it into Halloran Motors in Cinder Yards.`,
        hint: 'Parked cars are safest. Pulling a driver out of a car makes the police come faster.',
        blockedText: 'Halloran won’t take it badly damaged or with police watching. Repair it at a garage (the spanner on the map), or lose them.',
        onComplete: (m) => {
          const player = m.ctx.services.get('player');
          const v = player.vehicle;
          if (v) {
            m.vars.health = v.health / v.spec.maxHealth;
            m.vars.exportedId = v.id;
            v.tags.add('exported');
          }
        },
      },
    ],
    outroFocus: { poi: 'garage-halloran' },
    outro: [{ who: 'Des Halloran', text: EXPORT_OUTROS[((n % EXPORT_OUTROS.length) + EXPORT_OUTROS.length) % EXPORT_OUTROS.length]! }],
    payout: (m) => {
      const lines: RewardLine[] = [{ label: `${name}, shipped`, amount: order.price }];
      const h = typeof m.vars.health === 'number' ? m.vars.health : 0;
      if (h >= 0.9) lines.push({ label: 'Showroom condition', amount: Math.round(order.price * 0.25) });
      return lines;
    },
  };
}

export const MISSIONS: readonly MissionDef[] = [borrowedTime, wetPaper, gulletJob, oneArmedBandits, theCollection, lastOrders];
